import { otherCount, type OtherWork } from './workbench/otherWork'
import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigationParams } from './navigation/history'
import { apiFetch, API_MUTATION_EVENT } from '../lib/api'
import { useAuthSession } from '../lib/authSession'
import { humanizeToken } from '../lib/officerFormat'
import type { DocumentRef } from '../lib/documentReader'
import DocumentLink from './documents/DocumentLink'
import { ErrorState } from './state'
import FolderDocuments from './navigation/FolderDocuments'
import { WORK_QUEUES, WORK_QUEUE_LABELS } from './navigation/OpenItemsSummary'
import {
  workbenchHref,
  type ClientView,
  type ContextWork,
  type FundView,
} from './navigation/context'
import { useRead } from './navigation/useRead'
import './fund/fundtabs.css'
import './navigation/navigation.css'
import './today-open-items.css'

type Queues = Pick<ContextWork, 'queues' | 'book_discovery_complete' | 'book_discovery_issues'>
interface ReportingAction {
  id: string
  as_of: string | null
  due_date: string | null
  status: string
  ready: boolean
  signed_current: boolean
  historical_only: boolean
  can_sign_off: boolean
}
interface TodayFund extends Queues {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  client_hints: FundView['client_hints']
  open_subchain_count: number
  reporting_dates_missing: boolean
  reporting_actions: ReportingAction[]
  other_open_items: number
  other_work?: OtherWork
}
interface Attention {
  id: string
  kind: string
  source_id: string
  source_name: string | null
  client_id: string | null
  client_name: string | null
  document: DocumentRef | null
  receipt_id: string | null
  status: string
  reasons: string[]
  work_context?: { id: string; subject: string; queue: string; reason: string }[]
}
interface TodayRead extends Queues {
  book_discovery_documents?: DocumentRef[]
  book_discovery_has_unnamed_issues?: boolean
  funds: TodayFund[]
  open_subchain_count: number
  attention: Attention[]
  generated_at: string
  revision: string
}

function useToday(refresh: number) {
  const session = useAuthSession()
  const key = JSON.stringify([session, refresh])
  const [read, setRead] = useState<{ key: string; value?: TodayRead; error?: unknown } | null>(null)
  useEffect(() => {
    if (!session) return
    const controller = new AbortController()
    let pending = false
    let refreshAfterPending = false
    const pull = (background: boolean) => {
      if (controller.signal.aborted) return
      if (pending) {
        refreshAfterPending = true
        return
      }
      pending = true
      apiFetch<TodayRead>('/api/home/open-items', { signal: controller.signal, background })
        .then((value) => {
          if (!controller.signal.aborted) setRead({ key, value })
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted)
            setRead((previous) => ({
              key,
              value: previous?.key === key ? previous.value : undefined,
              error,
            }))
        })
        .finally(() => {
          pending = false
          if (refreshAfterPending && !controller.signal.aborted) {
            refreshAfterPending = false
            pull(true)
          }
        })
    }
    pull(false)
    // Programmatic refresh must not keep the officer's activity lease alive.
    const timer = setInterval(() => pull(true), 30_000)
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') pull(true)
    }
    const refreshAfterAction = () => pull(true)
    window.addEventListener('focus', refreshVisible)
    document.addEventListener('visibilitychange', refreshVisible)
    window.addEventListener(API_MUTATION_EVENT, refreshAfterAction)
    return () => {
      controller.abort()
      clearInterval(timer)
      window.removeEventListener('focus', refreshVisible)
      document.removeEventListener('visibilitychange', refreshVisible)
      window.removeEventListener(API_MUTATION_EVENT, refreshAfterAction)
    }
  }, [key, session])
  return read?.key === key ? read : null
}

function clientContext(fund: TodayFund) {
  const hints = [...new Map(fund.client_hints.map((hint) => [hint.client_id, hint])).values()]
  const unique = hints.length === 1 && hints[0].match_status === 'unique'
  return unique
    ? {
        key: hints[0].client_id,
        label: hints[0].client_name ?? 'Client name not recorded',
        needsReview: false,
      }
    : {
        key: hints.length ? 'ambiguous' : 'unlinked',
        label: hints.length ? 'Client link needs review' : 'Funds not linked to a client',
        needsReview: true,
      }
}
const reasonLabels: Record<string, string> = {
  fund_context_not_established: 'Review this document’s fund context',
  additional_work_context_not_established: 'Additional work needs fund context',
  intake_failed: 'Intake needs attention',
  intake_incomplete: 'Intake incomplete',
  awaiting_release: 'Ready to process',
  document_processing_needs_attention: 'Document processing needs attention',
  client_metadata_needs_review: 'Client details need review',
  fund_folder_context_needs_review: 'Fund folder context needs review',
  intake_source_needs_review: 'Source intake needs review',
}

export default function TodayOpenItems() {
  const [refresh, setRefresh] = useState(0)
  const [params, setParams] = useNavigationParams()
  const clientId = params.get('intake')
  const setClientId = (id: string | null) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous)
      if (id) next.set('intake', id)
      else next.delete('intake')
      return next
    })
  const [clientRefresh, setClientRefresh] = useState(0)
  const read = useToday(refresh)
  const client = useRead<ClientView>(
    clientId ? `/api/clients/${encodeURIComponent(clientId)}` : null,
    clientRefresh
  )
  const value = read?.value
  const hasQueueWork = value?.queues.some((queue) => queue.counts.card_count > 0) ?? false
  const discoveryIncomplete =
    value?.book_discovery_complete === false ||
    value?.funds.some((fund) => fund.book_discovery_complete === false) === true
  const groups = new Map<string, { label: string; needsReview: boolean; funds: TodayFund[] }>()
  for (const fund of value?.funds ?? []) {
    const context = clientContext(fund)
    const group = groups.get(context.key) ?? {
      label: context.label,
      needsReview: context.needsReview,
      funds: [],
    }
    group.funds.push(fund)
    groups.set(context.key, group)
  }
  return (
    <main className="cockpit fa-page today-open" data-testid="today-open-items">
      <div className="fa">
        <header className="fa-head today-open-head">
          <div>
            <h1 className="fa-name">Today</h1>
            <p>All client funds with open work</p>
          </div>
          <Link className="today-all-work" to={workbenchHref({ kind: 'all' }, 'book')}>
            View all work ›
          </Link>
        </header>
        {!read ? <p role="status">Loading open items…</p> : null}
        {read?.error ? (
          <>
            <ErrorState
              what="current open items"
              error={read.error}
              onRetry={() => setRefresh((n) => n + 1)}
            />
            {value ? (
              <p role="alert">
                The last loaded figures are shown. Current status could not be refreshed.
              </p>
            ) : null}
          </>
        ) : null}
        {value ? (
          <>
            {discoveryIncomplete ? (
              <p className="today-open-incomplete" role="status">
                Book counts may be incomplete.
                {value.book_discovery_documents?.length ? (
                  <span>
                    {' '}
                    Affected documents:{' '}
                    {value.book_discovery_documents.map((document, index) => (
                      <Fragment key={document.document_id}>
                        {index > 0 ? '; ' : null}
                        <DocumentLink document={document} context={{ returnTo: '/v2' }} />
                      </Fragment>
                    ))}
                    .
                  </span>
                ) : null}
                {value.book_discovery_has_unnamed_issues ||
                !value.book_discovery_documents?.length ? (
                  <span> Some affected items could not be identified.</span>
                ) : null}
              </p>
            ) : null}
            {value.funds.length ? (
              <section className="fa-card today-open-funds">
                <div className="fa-chd">
                  Funds with open work <span>{value.funds.length}</span>
                </div>
                <div className="today-open-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Fund</th>
                        {WORK_QUEUES.map((queue) => (
                          <th key={queue} className={queue} scope="col">
                            {WORK_QUEUE_LABELS[queue]}
                          </th>
                        ))}
                        <th className="other" scope="col">
                          Other
                        </th>
                      </tr>
                    </thead>
                    {[...groups.entries()].map(([id, group]) => (
                      <tbody key={id}>
                        <tr className="today-open-client">
                          <th colSpan={5}>
                            <div className="today-open-client-heading">
                              <span>{group.label}</span>
                              {group.needsReview ? (
                                <Link className="nav-link" to="/v2/funds">
                                  Review clients and folders ›
                                </Link>
                              ) : null}
                            </div>
                          </th>
                        </tr>
                        {group.funds.map((fund) => (
                          <tr key={fund.id} data-fund-id={fund.id}>
                            <th scope="row">
                              <Link
                                className="today-open-name"
                                to={workbenchHref({ kind: 'fund', fund_id: fund.id })}
                              >
                                {fund.name} ›
                              </Link>
                              {clientContext(fund).needsReview && fund.client_hints.length ? (
                                <small>
                                  {[
                                    ...new Set(
                                      fund.client_hints
                                        .map((hint) => hint.client_name)
                                        .filter(Boolean)
                                    ),
                                  ].join(' · ')}
                                </small>
                              ) : null}
                            </th>
                            {WORK_QUEUES.map((queue) => {
                              const count = fund.queues.find((item) => item.name === queue)?.counts
                                .card_count
                              return (
                                <td key={queue}>
                                  <Link
                                    className={`today-open-count ${queue}`}
                                    aria-label={`${fund.name}: ${count ?? 'unknown'} ${WORK_QUEUE_LABELS[queue]} items`}
                                    to={workbenchHref({ kind: 'fund', fund_id: fund.id }, queue)}
                                  >
                                    {count ?? '—'}
                                  </Link>
                                </td>
                              )
                            })}
                            <td>
                              <Link
                                className="today-open-count other"
                                to={workbenchHref({ kind: 'fund', fund_id: fund.id }, 'other')}
                                aria-label={`${fund.name}: ${fund.other_work?.count ?? 'unknown'} Other items${fund.other_work && !fund.other_work.complete ? '; list incomplete' : ''}`}
                              >
                                {otherCount(fund.other_work)}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ))}
                  </table>
                </div>
              </section>
            ) : (
              <section className="fa-card today-open-empty">
                <h2>
                  {hasQueueWork
                    ? 'Open work is available'
                    : discoveryIncomplete
                      ? 'No fund work loaded'
                      : 'No open fund work'}
                </h2>
                <p>
                  {hasQueueWork || discoveryIncomplete
                    ? 'View all work to review the available items.'
                    : 'There are no pending fund items to show.'}
                </p>
                <Link to="/v2/funds">Open Funds ›</Link>
              </section>
            )}
            {value.attention.length ? (
              <section className="fa-card today-open-attention">
                <div className="fa-chd">
                  Client attention <span>{value.attention.length}</span>
                </div>
                {value.attention.map((item) => (
                  <div className="today-attention-row" key={item.id}>
                    <div>
                      <strong>
                        {item.client_name ?? item.source_name ?? 'Client folder needs review'}
                      </strong>
                      {item.document ? (
                        <DocumentLink document={item.document} context={{ returnTo: '/v2' }} />
                      ) : null}
                      <small>
                        {item.reasons
                          .map((reason) => reasonLabels[reason] ?? humanizeToken(reason))
                          .join(' · ')}
                      </small>
                      {item.work_context?.map((work) => (
                        <small key={work.id}>
                          {WORK_QUEUE_LABELS[work.queue as keyof typeof WORK_QUEUE_LABELS] ??
                            'Context'}{' '}
                          · {work.subject} — {work.reason}
                        </small>
                      ))}
                    </div>
                    {item.client_id ? (
                      <button className="nav-link" onClick={() => setClientId(item.client_id)}>
                        Review client intake ›
                      </button>
                    ) : (
                      <Link to="/v2/traces">Open Traces ›</Link>
                    )}
                  </div>
                ))}
              </section>
            ) : null}
          </>
        ) : null}
        {clientId && client.loading ? <p role="status">Loading client intake…</p> : null}
        {clientId && client.error ? (
          <ErrorState
            what="client intake"
            error={client.error}
            onRetry={() => setClientRefresh((n) => n + 1)}
          />
        ) : null}
        {clientId && client.value ? (
          <FolderDocuments client={client.value} onClose={() => setClientId(null)} />
        ) : null}
      </div>
    </main>
  )
}
